import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Grid,
    IconButton
} from '@mui/material';
import {
    CardGiftcard,
    MonetizationOn,
    ShoppingCart,
    Star,
    Discount,
    DeleteOutline,
    LocalOffer,
    Close,
    SentimentDissatisfied,
    WarningAmber
} from '@mui/icons-material';
import Notification from '../../customComponents/notification';

export default function CouponSelector({ couponData, onSelect, selectedCoupon, getSumTotal }) {
    const [open, setOpen] = useState(false);

    // Stato per Notification
    const [status, setStatus] = useState({
        open: false,
        text: '',
        severity: 'warning',  // puoi cambiare in error/info/success
        timeout: 5000,
        noCloseButton: false,
        noMargin: false,
    });

    const spesaCount = couponData?.couponSpesa?.couponDisponibili ?? 0;
    const {
        active,
        active_spesa,
        active_fedelta,
    } = couponData?.program ?? {};

    const fedeltaCount = couponData?.couponFedelta?.couponDisponibiliDelivery ?? 0;

    const showCouponButton = active === 1 && (active_spesa === 1 || active_fedelta === 1);
    if (!showCouponButton) return null;

    const couponInUso = (couponData?.openCoupons?.spesa ?? 0) > 0 || (couponData?.openCoupons?.fedelta ?? 0) > 0;

    const {
        program: {
            valore_coupon,
            valore_coupon_fedelta,
        }
    } = couponData;

    const openDialog = () => setOpen(true);
    const closeDialog = () => setOpen(false);

    const handleSelect = (type, value) => {
        const total = getSumTotal();
        if (parseFloat(value) > total) {
            setStatus({
                open: true,
                text: `Il valore del coupon supera il totale dell’ordine. Puoi aggiungere altri prodotti per utilizzarlo!`,
                severity: 'warning',
                timeout: 5000,
                noCloseButton: false,
                noMargin: false,
            });
            return;
        }
        onSelect({ type, value });
        closeDialog();
    };



    return (
        <>
            {showCouponButton && (
                <Box textAlign="center" mb={2}>
                    {selectedCoupon ? (
                        <Box>
                            <Typography variant="body2" color="primary" display="flex" alignItems="center" justifyContent="center" gap={1}>
                                <Discount fontSize="small" />
                                Sconto attivo: <strong>{selectedCoupon.type === 'spesa' ? 'Coupon Spesa' : 'Coupon Fedeltà'}</strong> di €{selectedCoupon.value.replace('.', ',')}
                            </Typography>
                            <Button
                                variant="text"
                                color="error"
                                size="small"
                                startIcon={<DeleteOutline />}
                                onClick={() => onSelect(null)}
                            >
                                Rimuovi sconto
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<LocalOffer />}
                            onClick={openDialog}
                        >
                            Applica il tuo sconto
                        </Button>
                    )}
                </Box>
            )}

            <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <IconButton onClick={closeDialog}>
                            <Close />
                        </IconButton>
                        <Typography variant="h6">Seleziona un coupon</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>

                    {(spesaCount + fedeltaCount > 0) && couponInUso && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            bgcolor="background.paper"
                            color="warning.light"
                            px={2}
                            py={1.5}
                            borderRadius={2}
                            mb={3}
                        >
                            <WarningAmber />
                            <Typography variant="body2" fontWeight={'bold'}>
                                Hai già un ordine con un coupon attivo. Puoi usarne un altro solo quando sarà completato.
                            </Typography>
                        </Box>
                    )}

                    {spesaCount + fedeltaCount === 0 ? (
                        <Box
                            textAlign="center"
                            py={5}
                            px={3}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={1.5}
                            color="text.secondary"
                        >
                            <SentimentDissatisfied sx={{ fontSize: 48, color: 'grey.500' }} />
                            <Typography variant="h7" component="p" sx={{ fontWeight: 'medium' }}>
                                Al momento non hai coupon disponibili.
                            </Typography>
                            <Typography variant="h7" component="p" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                Continua ad acquistare per accumularli!
                            </Typography>
                            <Typography variant="body1" component="p" sx={{ mt: 2, color: 'text.secondary', maxWidth: 300 }}>
                                Vai nel tuo profilo, nella sezione Fidelity Card, per scoprire quanto ti manca.
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {active_spesa === 1 && Array.from({ length: spesaCount }).map((_, i) => (
                                <Grid item xs={12} key={`spesa-${i}`}>
                                    <Box
                                        mb={2}
                                        p={1}
                                        border="2px solid #2196f3"
                                        borderRadius={3}
                                        onClick={() => !couponInUso && handleSelect('spesa', valore_coupon.toFixed(2))}
                                        sx={{
                                            cursor: couponInUso ? 'not-allowed' : 'pointer',
                                            backgroundColor: 'background.paper',
                                            opacity: couponInUso ? 0.5 : 1,
                                            pointerEvents: couponInUso ? 'none' : 'auto',
                                        }}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <Box mr={2}>
                                                <CardGiftcard color="secondary" sx={{ fontSize: 36 }} />
                                            </Box>
                                            <Box flex={1}>
                                                <Box display="flex" alignItems="center" mb={1}>
                                                    <ShoppingCart sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }} />
                                                    <Typography variant="body2" color="textSecondary">
                                                        Coupon Spesa
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" mb={1}>
                                                    <MonetizationOn sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                                                    <Typography variant="h6" fontWeight="bold" color="primary">
                                                        Sconto €{valore_coupon.toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}

                            {active_fedelta === 1 && Array.from({ length: fedeltaCount }).map((_, i) => (
                                <Grid item xs={12} key={`fedelta-${i}`}>
                                    <Box
                                        mb={2}
                                        p={1}
                                        border="2px solid #2196f3"
                                        borderRadius={3}
                                        onClick={() => !couponInUso && handleSelect('fedelta', valore_coupon_fedelta.toFixed(2))}
                                        sx={{
                                            cursor: couponInUso ? 'not-allowed' : 'pointer',
                                            backgroundColor: 'background.paper',
                                            opacity: couponInUso ? 0.5 : 1,
                                            pointerEvents: couponInUso ? 'none' : 'auto',
                                        }}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <Box mr={2}>
                                                <CardGiftcard color="secondary" sx={{ fontSize: 36 }} />
                                            </Box>
                                            <Box flex={1}>
                                                <Box display="flex" alignItems="center" mb={1}>
                                                    <Star sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }} />
                                                    <Typography variant="body2" color="textSecondary">
                                                        Coupon Fedeltà
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" mb={1}>
                                                    <MonetizationOn sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                                                    <Typography variant="h6" fontWeight="bold" color="primary">
                                                        Sconto €{valore_coupon_fedelta.toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {/* Notification integrata */}
                    <Notification
                        status={status}
                        setStatus={setStatus}
                        bottom={true}
                    />
                </DialogContent>
            </Dialog >


        </>
    );
}
